import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { BookOpen, Heart, Cpu, Users, Coins, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_94b15709-33c0-4b3c-a307-474349afd7f8/artifacts/vb9rqttx_ChatGPT%20Image%20Feb%201%2C%202026%20at%2008_39_11%20AM.png";

const Landing = () => {
  const [email, setEmail] = useState('');
  const [stats, setStats] = useState(null);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [isSubscribing, setIsSubscribing] = useState(false);

  useEffect(() => {
    axios.get(`${API}/stats/ecosystem`).then(r => setStats(r.data)).catch(() => {});
    axios.get(`${API}/token/info`).then(r => setTokenInfo(r.data)).catch(() => {});
  }, []);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setIsSubscribing(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      toast.success('Successfully subscribed!');
      setEmail('');
    } catch (err) {
      toast.error('Failed to subscribe');
    }
    setIsSubscribing(false);
  };

  return (
    <div className="min-h-screen" data-testid="landing-page">
      <section className="relative min-h-screen flex items-center justify-center pt-20 pb-16 px-6">
        <div className="hero-gradient absolute inset-0" />
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <img src={LOGO_URL} alt="AIH" className="w-32 h-32 mx-auto mb-8 rounded-2xl gold-glow-strong float-animation" data-testid="hero-logo" />
          <h1 className="font-unbounded text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            <span className="text-white">ALL IN HAND</span><br />
            <span className="gradient-text">UNIVERSE</span>
          </h1>
          <p className="text-lg text-white/70 max-w-2xl mx-auto mb-8">
            A unified portal for work, learning, earning, creating & investing. Powered by AIH Token, backed by Bitcoin.
          </p>
          {tokenInfo && (
            <div className="inline-flex items-center gap-3 glass-panel px-6 py-3 mb-8" data-testid="token-badge">
              <Coins size={18} className="text-[#D4AF37]" />
              <span className="text-[#D4AF37] font-bold">${tokenInfo.price}</span>
              <span className="text-green-400 text-sm">LIVE</span>
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register"><Button className="btn-primary text-lg px-8 py-4" data-testid="hero-get-started">Get Started <ArrowRight className="ml-2" size={20} /></Button></Link>
            <a href="https://allinhandtokens.com" target="_blank" rel="noopener noreferrer"><Button className="btn-secondary text-lg px-8 py-4" data-testid="hero-buy-token">Buy AIH Token</Button></a>
          </div>
        </div>
      </section>

      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="stat-card text-center" data-testid="stat-total-users">
            <p className="stat-value">{stats?.total_users || '10,000+'}</p>
            <p className="stat-label">Total Users</p>
          </div>
          <div className="stat-card text-center" data-testid="stat-total-staked">
            <p className="stat-value">${(stats?.total_staked || 12500000).toLocaleString()}</p>
            <p className="stat-label">Total Staked</p>
          </div>
          <div className="stat-card text-center" data-testid="stat-treasury">
            <p className="stat-value">${(stats?.treasury_value || 2500000).toLocaleString()}</p>
            <p className="stat-label">Treasury</p>
          </div>
          <div className="stat-card text-center" data-testid="stat-burned">
            <p className="stat-value">{(stats?.buyback_burned || 150000).toLocaleString()}</p>
            <p className="stat-label">Burned</p>
          </div>
        </div>
      </section>

      <section className="py-20 px-6" data-testid="pillars-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-unbounded text-3xl font-bold text-white mb-4">The 5 Pillars of <span className="gradient-text">AIH Universe</span></h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            <div className="feature-card" data-testid="pillar-education">
              <div className="pillar-icon education mb-4"><BookOpen size={28} className="text-blue-400" /></div>
              <h3 className="font-unbounded font-bold text-white mb-2">Education</h3>
              <p className="text-sm text-white/60">Learn Web3, AI, Marketing</p>
            </div>
            <div className="feature-card" data-testid="pillar-wellness">
              <div className="pillar-icon health mb-4"><Heart size={28} className="text-green-400" /></div>
              <h3 className="font-unbounded font-bold text-white mb-2">Wellness</h3>
              <p className="text-sm text-white/60">Holistic health focus</p>
            </div>
            <div className="feature-card" data-testid="pillar-technology">
              <div className="pillar-icon technology mb-4"><Cpu size={28} className="text-purple-400" /></div>
              <h3 className="font-unbounded font-bold text-white mb-2">Technology</h3>
              <p className="text-sm text-white/60">AI-powered innovation</p>
            </div>
            <div className="feature-card" data-testid="pillar-community">
              <div className="pillar-icon community mb-4"><Users size={28} className="text-orange-400" /></div>
              <h3 className="font-unbounded font-bold text-white mb-2">Community</h3>
              <p className="text-sm text-white/60">Engage and earn together</p>
            </div>
            <div className="feature-card" data-testid="pillar-finance">
              <div className="pillar-icon finance mb-4"><Coins size={28} className="text-[#D4AF37]" /></div>
              <h3 className="font-unbounded font-bold text-white mb-2">Finance</h3>
              <p className="text-sm text-white/60">Bitcoin-backed AIH Token</p>
            </div>
          </div>
        </div>
      </section>

      {tokenInfo && tokenInfo.staking_tiers && (
        <section className="py-20 px-6" data-testid="staking-tiers-section">
          <div className="max-w-7xl mx-auto">
            <h2 className="font-unbounded text-3xl font-bold text-white mb-8 text-center">Staking <span className="gradient-text">Tiers</span></h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="glass-panel p-6 text-center" data-testid="tier-bronze">
                <span className="tier-badge bronze mb-4 inline-block">Bronze</span>
                <p className="font-unbounded text-3xl font-bold text-[#D4AF37] mb-2">8% APY</p>
                <p className="text-sm text-white/60">Min. 1,000 AIH</p>
              </div>
              <div className="glass-panel p-6 text-center" data-testid="tier-silver">
                <span className="tier-badge silver mb-4 inline-block">Silver</span>
                <p className="font-unbounded text-3xl font-bold text-[#D4AF37] mb-2">12% APY</p>
                <p className="text-sm text-white/60">Min. 10,000 AIH</p>
              </div>
              <div className="glass-panel p-6 text-center" data-testid="tier-gold">
                <span className="tier-badge gold mb-4 inline-block">Gold</span>
                <p className="font-unbounded text-3xl font-bold text-[#D4AF37] mb-2">18% APY</p>
                <p className="text-sm text-white/60">Min. 50,000 AIH</p>
              </div>
              <div className="glass-panel p-6 text-center" data-testid="tier-platinum">
                <span className="tier-badge platinum mb-4 inline-block">Platinum</span>
                <p className="font-unbounded text-3xl font-bold text-[#D4AF37] mb-2">25% APY</p>
                <p className="text-sm text-white/60">Min. 100,000 AIH</p>
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="py-20 px-6" data-testid="newsletter-section">
        <div className="max-w-3xl mx-auto glass-panel p-8 text-center">
          <h2 className="font-unbounded text-2xl font-bold text-white mb-4">Stay Updated</h2>
          <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
            <Input type="email" placeholder="Enter your email" value={email} onChange={(e) => setEmail(e.target.value)} className="input-cosmic flex-1" data-testid="newsletter-email" />
            <Button type="submit" className="btn-primary" disabled={isSubscribing} data-testid="newsletter-submit">{isSubscribing ? '...' : 'Subscribe'}</Button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Landing;
